import { useState } from "react";

import axios from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const subjectOptions = [
  "VCE Mathematical Methods",
  "VCE Specialist Mathematics",
];

const yearLevels = ["Year 10", "Year 11", "Year 12", "Graduate / Other"];
const targetScores = ["35+", "40+", "45+", "50", "Strengthen foundations", "Unsure yet"];
const contactOwnerOptions = ["Parent", "Student"];

const slugify = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const inquirySchema = z.object({
  studentName: z.string().min(2, "Please enter the student name."),
  parentName: z.string().min(2, "Please enter the parent full name."),
  yearLevel: z.string().min(1, "Please select a year level."),
  currentSubjects: z.array(z.string()).min(1, "Select at least one subject."),
  targetStudyScore: z.string().min(1, "Please select a target study score."),
  email: z.string().email("Please enter a valid email address."),
  emailContactOwner: z.string().min(1, "Please indicate who the email belongs to."),
  phone: z.string().min(8, "Please enter a valid phone number."),
  phoneContactOwner: z.string().min(1, "Please indicate who the phone number belongs to."),
  academicGoals: z.string().min(12, "Please share a brief academic goal."),
});

const defaultValues = {
  studentName: "",
  parentName: "",
  yearLevel: "",
  currentSubjects: [],
  targetStudyScore: "",
  email: "",
  emailContactOwner: "",
  phone: "",
  phoneContactOwner: "",
  academicGoals: "",
};

export const InquiryForm = ({ contactDetails }) => {
  const [successMessage, setSuccessMessage] = useState("");

  const form = useForm({
    resolver: zodResolver(inquirySchema),
    defaultValues,
  });

  const toggleSubject = (subject, selectedSubjects, onChange) => {
    const updatedSubjects = selectedSubjects.includes(subject)
      ? selectedSubjects.filter((item) => item !== subject)
      : [...selectedSubjects, subject];

    onChange(updatedSubjects);
  };

  const onSubmit = async (values) => {
    try {
      const response = await axios.post(`${API}/inquiries`, {
        student_name: values.studentName,
        parent_name: values.parentName,
        year_level: values.yearLevel,
        current_subjects: values.currentSubjects,
        target_study_score: values.targetStudyScore,
        email: values.email,
        email_contact_owner: values.emailContactOwner,
        phone: values.phone,
        phone_contact_owner: values.phoneContactOwner,
        academic_goals: values.academicGoals,
      });

      setSuccessMessage(response.data.message);
      toast.success("Enquiry submitted", {
        description: "We will contact you to arrange a free information session.",
      });
      form.reset(defaultValues);
    } catch (error) {
      setSuccessMessage("");
      toast.error("Unable to submit enquiry", {
        description: "Please check your details or contact us directly using the details on this page.",
      });
      console.error(error);
    }
  };

  return (
    <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="inquiry-form-card">
      <CardContent className="p-8">
        <div className="mb-8 space-y-4">
          <p className="eyebrow text-[#002147]/65" data-testid="inquiry-form-overline">
            Request an information session
          </p>
          <h3 className="font-display text-4xl tracking-tight text-[#002147]" data-testid="inquiry-form-heading">
            Tell us about the student, parent, and academic goals.
          </h3>
          <p className="text-sm leading-7 text-slate-700" data-testid="inquiry-form-description">
            Submit the form below and Ascend Academy will follow up to organise the next step.
            You can also contact us directly via {contactDetails.email} or {contactDetails.phone}.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-5" data-testid="inquiry-form">
            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                control={form.control}
                name="studentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-name">Student name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter the student’s full name"
                        className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus-visible:ring-[#002147]/20"
                        data-testid="input-name"
                      />
                    </FormControl>
                    <FormMessage data-testid="error-name" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="parentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-parent-name">Parent full name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter the parent’s full name"
                        className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus-visible:ring-[#002147]/20"
                        data-testid="input-parent-name"
                      />
                    </FormControl>
                    <FormMessage data-testid="error-parent-name" />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                control={form.control}
                name="yearLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-year">Year level</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger
                          className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus:ring-[#002147]/20"
                          data-testid="input-year"
                        >
                          <SelectValue placeholder="Select year level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {yearLevels.map((level) => (
                          <SelectItem key={level} value={level} data-testid={`year-option-${slugify(level)}`}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage data-testid="error-year" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="targetStudyScore"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-score">Target study score</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger
                          className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus:ring-[#002147]/20"
                          data-testid="input-score"
                        >
                          <SelectValue placeholder="Select target score" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {targetScores.map((score) => (
                          <SelectItem key={score} value={score} data-testid={`score-option-${slugify(score)}`}>
                            {score}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage data-testid="error-score" />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="currentSubjects"
              render={({ field }) => (
                <FormItem>
                  <FormLabel data-testid="label-subjects">Current subjects</FormLabel>
                  <div className="grid gap-3 md:grid-cols-2" data-testid="input-subjects">
                    {subjectOptions.map((subject) => {
                      const selected = field.value.includes(subject);
                      const testId = `subject-option-${slugify(subject)}`;

                      return (
                        <button
                          key={subject}
                          type="button"
                          onClick={() => toggleSubject(subject, field.value, field.onChange)}
                          className={`min-h-[64px] border px-4 py-3 text-left text-sm font-medium transition-colors duration-300 ${
                            selected
                              ? "border-[#002147] bg-[#002147] text-white"
                              : "border-[#002147]/15 bg-[#fcfbf8] text-[#173458] hover:border-[#002147]/35"
                          }`}
                          data-testid={testId}
                        >
                          {subject}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#002147]/55" data-testid="subjects-future-note">
                    Additional subjects will be introduced in future intakes.
                  </p>
                  <FormMessage data-testid="error-subjects" />
                </FormItem>
              )}
            />

            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-email">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="student-or-parent@email.com"
                        className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus-visible:ring-[#002147]/20"
                        data-testid="input-email"
                      />
                    </FormControl>
                    <FormMessage data-testid="error-email" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailContactOwner"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-email-owner">Email belongs to</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger
                          className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus:ring-[#002147]/20"
                          data-testid="input-email-owner"
                        >
                          <SelectValue placeholder="Select contact owner" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {contactOwnerOptions.map((owner) => (
                          <SelectItem key={owner} value={owner} data-testid={`email-owner-option-${slugify(owner)}`}>
                            {owner}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage data-testid="error-email-owner" />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-phone">Phone</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter the best contact number"
                        className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus-visible:ring-[#002147]/20"
                        data-testid="input-phone"
                      />
                    </FormControl>
                    <FormMessage data-testid="error-phone" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneContactOwner"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel data-testid="label-phone-owner">Phone belongs to</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger
                          className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus:ring-[#002147]/20"
                          data-testid="input-phone-owner"
                        >
                          <SelectValue placeholder="Select contact owner" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {contactOwnerOptions.map((owner) => (
                          <SelectItem key={owner} value={owner} data-testid={`phone-owner-option-${slugify(owner)}`}>
                            {owner}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage data-testid="error-phone-owner" />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="academicGoals"
              render={({ field }) => (
                <FormItem>
                  <FormLabel data-testid="label-goals">Academic goals</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={5}
                      placeholder="Share the student’s goals, current challenges, or the type of outcome they are aiming for."
                      className="rounded-sm border-[#002147]/15 bg-[#fcfbf8] focus-visible:ring-[#002147]/20"
                      data-testid="input-goals"
                    />
                  </FormControl>
                  <FormMessage data-testid="error-goals" />
                </FormItem>
              )}
            />

            {successMessage ? (
              <div
                className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
                aria-live="polite"
                data-testid="inquiry-success-message"
              >
                {successMessage}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="rounded-sm bg-[#002147] px-6 text-white hover:bg-[#001530]"
              data-testid="submit-inquiry-button"
            >
              {form.formState.isSubmitting ? "Submitting enquiry..." : "Request Information Session"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};